/**
 * apiErrors — the one place an HTTP failure is turned into something a person
 * can read (PR-A, 8 Sep 2026; NFT-8 of the 3 Sep click-walk).
 *
 * Before this, lib/api.js handled 401 refresh and CSRF retry and nothing else:
 * a 429 rendered as an empty dropdown, a 500 as "No results found", a 400 as
 * "Failed to create event" with no field named. The user could not tell
 * "nothing here" from "the platform failed".
 *
 * This module is framework-free (lib/api.js is not a React module). It emits a
 * DOM event that ToastContext subscribes to, de-duplicated so a burst of
 * failing requests produces one toast, not twenty.
 */

export const API_ERROR_EVENT = 'igrc:api-error'

const DEDUPE_MS = 8000
const lastShown = new Map()

/** Publish a user-facing API error. `kind` is the dedupe key. */
export function announceApiError({ kind, message, correlationId, retryAfterSeconds }) {
  if (typeof window === 'undefined') return
  const now = Date.now()
  const key = `${kind}:${message}`
  if ((lastShown.get(key) || 0) + DEDUPE_MS > now) return
  lastShown.set(key, now)
  window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, {
    detail: { kind, message, correlationId: correlationId || null, retryAfterSeconds: retryAfterSeconds || null },
  }))
}

/** Read Retry-After (seconds or HTTP-date) or the body's retryAfterSeconds. */
export function retryAfterFrom(error) {
  const header = error?.response?.headers?.['retry-after']
  if (header) {
    const n = Number(header)
    if (Number.isFinite(n)) return Math.max(1, n)
    const t = Date.parse(header)
    if (!Number.isNaN(t)) return Math.max(1, Math.ceil((t - Date.now()) / 1000))
  }
  const body = error?.response?.data?.error?.retryAfterSeconds
  return Number.isFinite(body) ? body : null
}

/**
 * Attach a normalised description to the axios error and announce it.
 * Returns the (mutated) error for the caller to reject with.
 */
export function describeApiError(error) {
  const status = error?.response?.status
  const data = error?.response?.data
  const correlationId = data?.error?.correlationId || data?.correlationId || null

  if (status === 429) {
    const secs = retryAfterFrom(error)
    error.userMessage = secs
      ? `The platform is busy — please wait ${secs} second${secs === 1 ? '' : 's'} and try again.`
      : 'The platform is busy — please try again in a moment.'
    error.kind = 'rate_limited'
    announceApiError({ kind: 'rate_limited', message: error.userMessage, retryAfterSeconds: secs })
    return error
  }

  if (status >= 500) {
    error.userMessage = correlationId
      ? `Something went wrong on our side (ref ${correlationId.slice(0, 8)}). Please try again; quote the ref if it persists.`
      : 'Something went wrong on our side. Please try again.'
    error.kind = 'server_fault'
    announceApiError({ kind: 'server_fault', message: error.userMessage, correlationId })
    return error
  }

  if (status === 400 && data?.error?.code === 'VALIDATION_ERROR') {
    // details.body is { field: [messages] } from the zod validator.
    const fields = data?.error?.details?.body || data?.error?.details || {}
    error.fieldErrors = fields
    const first = Object.entries(fields)[0]
    error.userMessage = first
      ? `${first[0]}: ${Array.isArray(first[1]) ? first[1].join(', ') : String(first[1])}`
      : (data?.error?.message || 'Please check the form and try again.')
    error.kind = 'validation'
    // Not announced globally — the form that made the request owns the message.
    return error
  }

  if (!status) {
    // Network failure / timeout — axios gives no response.
    error.userMessage = 'We could not reach the platform. Check your connection and try again.'
    error.kind = 'network'
    announceApiError({ kind: 'network', message: error.userMessage })
    return error
  }

  error.kind = 'client'
  error.userMessage = data?.error?.message || data?.message || error.message
  return error
}
